import { useRef, useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '51吃瓜网',
    url: 'https://51chigua.com',
    description: '全网最新最热的吃瓜资讯平台。51吃瓜网提供全网最新入口、轻量版和APP客户端下载。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@51chigua.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '51吃瓜网有什么特色？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '51吃瓜网聚合全网最新最热的吃瓜资讯，涵盖娱乐八卦、网红动态、社会热点等多个领域。支持手机网页版和轻量版APP，随时随地浏览最新内容。',
        },
      },
      {
        '@type': 'Question',
        name: '如何下载51吃瓜轻量版APP？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过51吃瓜官网的下载入口获取轻量版APP客户端，支持安卓和iOS双平台。轻量版APP占用空间小、加载速度快，提供更流畅的吃瓜体验。',
        },
      },
      {
        '@type': 'Question',
        name: '51吃瓜网的内容更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '51吃瓜网的内容团队7×24小时不间断监测全网热点，热门内容实时更新，常规内容每日更新。我们确保用户第一时间获取最新吃瓜资讯。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系51吃瓜网？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过以下方式联系我们：合作邮箱 contact@51chigua.com，广告投放 ad@51chigua.com，内容投稿 submit@51chigua.com。或者直接使用本页面的联系表单。',
        },
      },
    ],
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = formRef.current;
    if (!form) return;

    const formData = new FormData(form);
    const honeypot = formData.get('mobile_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('mobile_alt');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === 'string') {
          params.append(key, value);
        }
      });
      const response = await fetch('https://readdy.ai/api/form/d9oqo5qqs38sj6jc7du0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const msg = parsed?.meta?.message || parsed?.message || '';
      const isSpam = msg.toLowerCase().includes('spam') || responseText.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setFormStatus('success');
        form.reset();
      } else {
        throw new Error(msg || '提交失败，请稍后重试');
      }
    } catch (err: unknown) {
      setFormStatus('error');
      const message = err instanceof Error ? err.message : '提交失败，请稍后重试';
      setFormError(message);
    }
  };

  return (
    <>
      <title>关于我们 - 51吃瓜网</title>
      <meta name="description" content="51吃瓜网品牌介绍、口碑评价和联系方式。了解51吃瓜网的发展历程、核心优势和用户口碑，获取51吃瓜官网入口、轻量版APP下载方式。" />
      <meta name="keywords" content="51吃瓜网,关于我们,品牌介绍,联系方式,51吃瓜官网,轻量版app下载,用户口碑" />
      <link rel="canonical" href="https://51chigua.com/about" />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <main>
        <Navbar />

        {/* Page Header */}
        <section className="pt-20 pb-8 md:pb-10 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 mt-4">关于51吃瓜网</h1>
            <p className="text-sm md:text-base text-foreground-600 mt-3 max-w-2xl">
              全网最新最热的吃瓜资讯平台——了解我们的品牌故事、核心优势和用户口碑
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">
                  品牌介绍
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>
                    <strong>51吃瓜网</strong>成立于2023年，是中文互联网上领先的娱乐八卦与热点资讯聚合平台。我们致力于打造一个集<strong>娱乐圈八卦、网红主播动态、明星恋情追踪、社会热点评论</strong>于一体的综合内容社区。
                  </p>
                  <p>
                    我们的编辑团队由资深媒体人和互联网从业者组成，全天候监测全网热点动态，确保第一时间为用户呈现最新、最全、最热的"吃瓜"内容。无论是顶流明星的最新动态，还是网红主播的翻车现场，亦或是引发全民热议的社会事件，51吃瓜网都会在第一时间为您深度呈现。
                  </p>
                  <p>
                    为了给用户提供更流畅的阅读体验，我们推出了<strong>51吃瓜轻量版APP</strong>和<strong>手机端网页版</strong>。轻量版APP占用空间小、加载速度快，即使在网络不佳的环境下也能流畅浏览。网页版则支持通过手机浏览器直接访问官网入口，无需下载安装。
                  </p>
                  <p>
                    我们的使命是：<strong>让每一个"瓜"都被看见，让每一次"吃瓜"都是一种享受。</strong>
                  </p>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20space%20with%20warm%20lighting%2C%20creative%20team%20working%20on%20laptops%2C%20large%20screens%20displaying%20content%2C%20plants%20decor%2C%20natural%20light%20from%20windows%2C%20editorial%20photography%2C%20warm%20wood%20tones%2C%20professional%20yet%20relaxed%20atmosphere%2C%20soft%20shadows%2C%20high%20detail&width=800&height=600&seq=about-brand-01&orientation=landscape"
                  alt="51吃瓜网品牌办公环境"
                  className="w-full h-full object-cover"
                  title="51吃瓜网品牌 - 办公环境"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Core Advantages */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 text-center mb-8">
              为什么选择51吃瓜网？
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                {
                  icon: 'ri-flashlight-line',
                  title: '极速更新',
                  desc: '7×24小时全天候实时监测全网热点，热门事件发生后最短时间内为您呈现，绝不让您错过任何一个"瓜"。',
                },
                {
                  icon: 'ri-smartphone-line',
                  title: '多端覆盖',
                  desc: '支持网页版、手机网页版和轻量版APP客户端，随时随地畅享吃瓜乐趣，不同设备无缝切换。',
                },
                {
                  icon: 'ri-database-2-line',
                  title: '内容全面',
                  desc: '涵盖娱乐八卦、网红主播、明星恋情、社会热点、影视综艺、搞笑趣闻等十多个内容板块。',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '轻量流畅',
                  desc: '轻量版APP占用空间极小，加载速度飞快，流量消耗低，让您省心省力地获取最新吃瓜资讯。',
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-background-100 rounded-xl p-6 border border-background-200/70 text-center"
                >
                  <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary-100 flex items-center justify-center">
                    <i className={`${item.icon} text-primary-500 text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-base text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* User Reviews */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 text-center mb-2">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              听听吃瓜群众怎么说
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  name: '吃瓜小能手',
                  rating: 5,
                  text: '自从用了51吃瓜网，再也不担心错过娱乐圈的大瓜了！轻量版APP特别好用，加载快还不占内存，每天坐地铁的时候刷刷，一个小时就过去了。',
                },
                {
                  name: '八卦协会会长',
                  rating: 5,
                  text: '内容更新速度真的快，经常比其他平台早好几个小时。文章质量也不错，不是那种标题党，每次点进去都有干货。推荐给所有爱吃瓜的姐妹！',
                },
                {
                  name: '热瓜追踪器',
                  rating: 4,
                  text: '网页版在手机上浏览体验很好，不需要下载APP也能流畅阅读。分类做得也很清晰，想看什么类型的直接点标签就行。希望能多加点搞笑分类的内容。',
                },
              ].map((review, index) => (
                <div key={index} className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className={`text-sm ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    "{review.text}"
                  </p>
                  <p className="text-xs text-foreground-500 font-medium">— {review.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[720px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 text-center mb-2">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              有任何问题或合作意向，欢迎通过以下方式联系
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              {[
                { icon: 'ri-mail-line', label: '合作邮箱', value: 'contact@51chigua.com' },
                { icon: 'ri-advertisement-line', label: '广告投放', value: 'ad@51chigua.com' },
                { icon: 'ri-edit-line', label: '内容投稿', value: 'submit@51chigua.com' },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-background-100 rounded-xl p-4 border border-background-200/70 text-center"
                >
                  <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-primary-100 flex items-center justify-center">
                    <i className={`${item.icon} text-primary-500`}></i>
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">{item.label}</p>
                  <p className="text-sm text-foreground-950 font-medium">{item.value}</p>
                </div>
              ))}
            </div>

            <form
              ref={formRef}
              id="contact-form"
              name="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-background-100 rounded-xl p-6 border border-background-200/70 space-y-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    placeholder="请输入您的姓名"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    placeholder="请输入您的邮箱"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题 <span className="text-primary-500">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  placeholder="请输入消息主题"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="请输入您的留言（500字以内）"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                />
                <p className="text-xs text-foreground-400 mt-1">限500字以内</p>
              </div>

              {/* Honeypot anti-spam */}
              <input
                type="text"
                name="mobile_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="spam-protection-field"
              />

              <button
                type="submit"
                className="w-full sm:w-auto px-8 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                发送消息
              </button>

              {formStatus === 'success' && (
                <p className="text-sm text-secondary-600 font-medium">消息已成功发送，我们会尽快与您联系！</p>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-primary-600 font-medium">{formError || '提交失败，请稍后重试'}</p>
              )}
            </form>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}